/**
 * Modal para crear órdenes de compra desde recursos de presupuesto de costo
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import { ordenCompraService, detalleOrdenCompraService, proveedorService, presupuestoCostoService } from '../services/onebaseService';
import bodegaService from '../services/bodegaService';
import './CrearModal.css';

const CrearOrdenCompraOneBaseModal = ({
  presupuesto,
  itemsSeleccionados,
  ordenCompraEditando,
  onClose,
  onCreated
}) => {
  const [proveedores, setProveedores] = useState([]);
  const [selectedProveedor, setSelectedProveedor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [itemsData, setItemsData] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [formData, setFormData] = useState({
    descripcion: '',
    fecha_emision: new Date().toISOString().split('T')[0],
    tipo_documento: '',
  });

  useEffect(() => {
    cargarProveedores();
    cargarBodegas();
    if (ordenCompraEditando) {
      inicializarDatosEdicion();
    } else {
      inicializarItemsData();
    }
  }, []);

  // useEffect para establecer el proveedor cuando los proveedores estén cargados
  useEffect(() => {
    if (ordenCompraEditando && ordenCompraEditando.proveedor && proveedores.length > 0) {
      const proveedor = proveedores.find(p => p.id === ordenCompraEditando.proveedor);
      if (proveedor) {
        setSelectedProveedor(proveedor);
      }
    }
  }, [proveedores, ordenCompraEditando]);

  const cargarProveedores = async () => {
    try {
      const response = await proveedorService.getAll({ status: 'activo' });
      let proveedoresData = [];

      if (response.data && response.data.results && Array.isArray(response.data.results)) {
        proveedoresData = response.data.results;
      } else if (Array.isArray(response.data)) {
        proveedoresData = response.data;
      }

      setProveedores(proveedoresData);
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
      toast.error('Error al cargar proveedores');
    }
  };

  const cargarBodegas = async () => {
    try {
      const response = await bodegaService.getBodegas({ status: 'activo' });
      let bodegasData = [];

      if (response.data && response.data.results && Array.isArray(response.data.results)) {
        bodegasData = response.data.results;
      } else if (Array.isArray(response.data)) {
        bodegasData = response.data;
      }

      setBodegas(bodegasData);
    } catch (error) {
      console.error('Error al cargar bodegas:', error);
      toast.error('Error al cargar bodegas');
    }
  };

  const inicializarItemsData = async () => {
    try {
      const itemsTemp = [];

      // IMPORTANTE: Si presupuesto.id es 0, necesitamos obtener el ID real del presupuesto de costo
      let presupuestoCostoId = presupuesto.id;

      if (!presupuestoCostoId || presupuestoCostoId === 0) {
        console.warn(`⚠️ Presupuesto ID es ${presupuestoCostoId}, intentando obtener el presupuesto activo del proyecto...`);
        // Si no tenemos un ID válido, intentar obtener el presupuesto activo del proyecto
        if (presupuesto.proyecto) {
          try {
            const presupuestoResponse = await presupuestoCostoService.getAll({
              proyecto: presupuesto.proyecto,
              status: 'activo',
              limit: 1
            });
            const presupuestos = presupuestoResponse.data?.results || presupuestoResponse.data || [];
            if (presupuestos.length > 0) {
              presupuestoCostoId = presupuestos[0].id;
              console.log(`✅ Presupuesto de costo encontrado: ID ${presupuestoCostoId}`);
            } else {
              console.warn('⚠️ No se encontró un presupuesto de costo activo para el proyecto');
            }
          } catch (error) {
            console.error('Error al buscar presupuesto de costo:', error);
          }
        }
      }

      for (const recurso of itemsSeleccionados) {
        // Cantidad presupuestada del recurso (usa cantidad_presupuestada_costo)
        const cantidadPresupuestada = parseFloat(recurso.cantidad_presupuestada_costo || recurso.cantidad_presupuesto || 0);

        // Usar las cantidades que vienen calculadas del backend
        // El serializer ya calcula correctamente OC + Salidas de bodega para este PresupuestoCostoRecurso
        const cantidadSolicitada = parseFloat(recurso.cantidad_solicitada || 0);
        // La cantidad disponible también viene calculada del backend
        const cantidadDisponible = parseFloat(recurso.cantidad_disponible || 0);

        /* CÓDIGO ANTERIOR: El cálculo ahora se hace en el backend
        if (recurso.recurso_referencia || recurso.nombre) {
          try {
            // 1. ÓRDENES DE COMPRA - Construir filtros específicos del proyecto
            const filtrosOC = {};

            // Filtrar por proyecto (OBLIGATORIO para evitar mezclar proyectos)
            if (presupuesto.proyecto && presupuesto.proyecto > 0) {
              filtrosOC.proyecto = presupuesto.proyecto;
            }

            // Buscar en detalles de órdenes de compra CON FILTROS ESPECÍFICOS
            const ocResponse = await detalleOrdenCompraService.getAll(filtrosOC);

            // Sumar cantidades de OCs SOLO de este recurso específico en este proyecto/presupuesto
            let cantidadOC = 0;
            if (ocResponse.data && ocResponse.data.results) {
              cantidadOC = ocResponse.data.results
                .filter(detalle => {
                  // IMPORTANTE: Filtrar por recurso_presupuesto_costo para asegurar que sea de la misma partida
                  // Si el detalle tiene recurso_presupuesto_costo, debe coincidir con el ID del recurso actual
                  if (detalle.recurso_presupuesto_costo && recurso.id) {
                    return detalle.recurso_presupuesto_costo === recurso.id;
                  }
                  // Si no tiene recurso_presupuesto_costo, filtrar por nombre (fallback para OCs antiguas)
                  return detalle.descripcion === recurso.nombre;
                })
                .reduce((sum, detalle) => sum + parseFloat(detalle.cantidad || 0), 0);
            }

            console.log(`🛒 Cantidad total de OCs para recurso ${recurso.nombre} (ID: ${recurso.id}): ${cantidadOC}`);

            // 2. SALIDAS DE BODEGA - Buscar solicitudes aprobadas
            let cantidadBodega = 0;
            try {
              console.log(`\n🔍 ===== BÚSQUEDA SALIDAS DE BODEGA: "${recurso.nombre}" =====`);
              console.log(`🔍 Recurso ID: ${recurso.id} | Proyecto: ${presupuesto.proyecto}`);

              // DIAGNÓSTICO: Ver TODAS las solicitudes del sistema
              console.log('\n📊 DIAGNÓSTICO: Obteniendo TODAS las solicitudes de bodega...');
              const todasSolicitudes = await bodegaService.getSolicitudesBodega({});
              const totalSolicitudes = todasSolicitudes.data?.count || todasSolicitudes.data?.results?.length || 0;
              console.log(`📊 Total solicitudes en el sistema: ${totalSolicitudes}`);

              if (totalSolicitudes > 0 && todasSolicitudes.data?.results) {
                console.log('📊 Estados de las solicitudes:');
                const estadosCuenta = {};
                todasSolicitudes.data.results.forEach(sol => {
                  estadosCuenta[sol.estado] = (estadosCuenta[sol.estado] || 0) + 1;
                });
                Object.entries(estadosCuenta).forEach(([estado, count]) => {
                  console.log(`   - ${estado}: ${count} solicitudes`);
                });
              }

              // Buscar solo las aprobadas
              const filtrosSolicitud = {
                estado: 'aprobada'
              };

              console.log('\n🔍 Buscando solicitudes APROBADAS...');
              const solicitudesResponse = await bodegaService.getSolicitudesBodega(filtrosSolicitud);

              if (solicitudesResponse.data && solicitudesResponse.data.results) {
                console.log(`📦 Total solicitudes aprobadas en el sistema: ${solicitudesResponse.data.results.length}`);

                // Filtrar solicitudes del mismo proyecto/evento
                const solicitudesFiltradas = solicitudesResponse.data.results.filter(solicitud => {
                  const proyectoSolicitud = solicitud.tarea_evento_data?.evento_data?.proyecto;
                  console.log(`📦 Solicitud ${solicitud.id} - Proyecto: ${proyectoSolicitud} (esperado: ${presupuesto.proyecto})`);
                  return proyectoSolicitud === presupuesto.proyecto;
                });

                console.log(`📦 Solicitudes filtradas por proyecto ${presupuesto.proyecto}: ${solicitudesFiltradas.length}`);

                // Sumar cantidades de los detalles que coincidan con el recurso
                solicitudesFiltradas.forEach((solicitud, idx) => {
                  console.log(`\n📦 Procesando solicitud ${idx + 1}/${solicitudesFiltradas.length} (ID: ${solicitud.id})`);

                  if (solicitud.detalles && Array.isArray(solicitud.detalles)) {
                    console.log(`📦 Detalles en solicitud ${solicitud.id}: ${solicitud.detalles.length} items`);

                    solicitud.detalles.forEach((detalle, detalleIdx) => {
                      const nombreDetalle = detalle.recurso_nombre || '';
                      const nombreRecurso = recurso.nombre || '';

                      // IMPORTANTE: Priorizar el match por recurso_presupuesto_costo si existe
                      let match = false;
                      if (detalle.recurso_presupuesto_costo && recurso.id) {
                        // Si existe el campo recurso_presupuesto_costo, debe coincidir exactamente
                        match = detalle.recurso_presupuesto_costo === recurso.id;
                      } else {
                        // Si no existe, usar el nombre como fallback
                        match = nombreDetalle === nombreRecurso;
                      }

                      console.log(`📦 Detalle ${detalleIdx + 1}:`);
                      console.log(`   - Nombre en detalle: "${nombreDetalle}"`);
                      console.log(`   - Nombre buscado: "${nombreRecurso}"`);
                      console.log(`   - ID recurso presupuesto en detalle: ${detalle.recurso_presupuesto_costo}`);
                      console.log(`   - ID recurso presupuesto buscado: ${recurso.id}`);
                      console.log(`   - ¿Coincide? ${match}`);

                      if (match) {
                        const cantidad = parseFloat(detalle.cantidad_aprobada || detalle.cantidad_solicitada || 0);
                        console.log(`   ✅ MATCH ENCONTRADO! Cantidad: ${cantidad}`);
                        console.log(`   - cantidad_aprobada: ${detalle.cantidad_aprobada}`);
                        console.log(`   - cantidad_solicitada: ${detalle.cantidad_solicitada}`);
                        cantidadBodega += cantidad;
                      }
                    });
                  } else {
                    console.log(`📦 Solicitud ${solicitud.id} NO tiene detalles o no es un array`);
                  }
                });

                console.log(`\n📦 ===== RESULTADO FINAL =====`);
                console.log(`📦 Cantidad total de bodega para "${recurso.nombre}": ${cantidadBodega}`);
                console.log(`📦 =============================\n`);
              } else {
                console.log('📦 No se encontraron solicitudes o el formato de respuesta es incorrecto');
              }
            } catch (errorBodega) {
              console.error(`❌ Error al calcular salidas de bodega para ${recurso.nombre}:`, errorBodega);
              console.error('❌ Stack trace:', errorBodega.stack);
            }

            // SUMAR ambas cantidades (OC + Salidas de bodega)
            cantidadSolicitada = cantidadOC + cantidadBodega;

          } catch (error) {
            console.warn(`No se pudo calcular cantidades solicitadas para ${recurso.nombre}`, error);
          }
        }

        // Cantidad disponible
        const cantidadDisponible = Math.max(0, cantidadPresupuestada - cantidadSolicitada);
        */

        itemsTemp.push({
          item: {
            ...recurso,
            area_id: recurso.area_id,
            partida_id: recurso.partida_id
          },
          cantidad: recurso.cantidad_inicial || cantidadDisponible, // Usar cantidad de la solicitud o cantidad disponible
          precio_unitario: recurso.precio_unitario_costo || 0,
          bodega: null, // Campo para seleccionar bodega (si tiene stock)
          tiene_stock: recurso.recurso_referencia_data?.stock || false, // Verificar si el recurso tiene stock
          // Info adicional para mostrar
          cantidadPresupuestada,
          cantidadSolicitada,
          cantidadDisponible,
          area_nombre: recurso.area_nombre,
          partida_nombre: recurso.partida_nombre
        });
      }

      setItemsData(itemsTemp);
    } catch (error) {
      console.error('Error al inicializar items:', error);
      toast.error('Error al cargar los recursos');
    }
  };

  const inicializarDatosEdicion = () => {
    if (!ordenCompraEditando) return;

    // Establecer los datos del formulario desde la OC
    setFormData({
      descripcion: ordenCompraEditando.descripcion || '',
      fecha_emision: ordenCompraEditando.fecha_emision || new Date().toISOString().split('T')[0],
      tipo_documento: ordenCompraEditando.tipo_documento || '',
    });

    // El proveedor se establecerá automáticamente mediante el useEffect
    // cuando los proveedores estén cargados

    // Inicializar los items con los datos de la OC
    const itemsTemp = itemsSeleccionados.map(item => {
      // Si el item tiene datos precargados de la OC (cantidadOC, etc)
      if (item.cantidadOC !== undefined) {
        const cantidadPresupuestada = parseFloat(item.cantidad) || 0;
        const cantidadEjecutada = parseFloat(item.cantidad_real) || 0;
        const cantidadPendiente = Math.max(0, cantidadPresupuestada - cantidadEjecutada);

        return {
          item,
          cantidad: parseFloat(item.cantidadOC) || 0,
          precio_unitario: parseFloat(item.precioUnitarioOC) || 0,
          cantidad_dias: parseFloat(item.diasOC) || 1,
          detalleOCId: item.detalleOCId,
          cantidadPresupuestada,
          cantidadEjecutada,
          cantidadPendiente
        };
      }

      // Si no tiene datos de la OC, usar valores por defecto
      const cantidadPresupuestada = parseFloat(item.cantidad) || 0;
      const cantidadEjecutada = parseFloat(item.cantidad_real) || 0;
      const cantidadPendiente = Math.max(0, cantidadPresupuestada - cantidadEjecutada);

      return {
        item,
        cantidad: cantidadPendiente,
        precio_unitario: item.costo_unitario || item.precio_unitario || 0,
        cantidadPresupuestada,
        cantidadEjecutada,
        cantidadPendiente
      };
    });
    setItemsData(itemsTemp);
  };

  const handleProveedorChange = (e) => {
    const proveedorId = e.target.value;
    if (proveedorId) {
      const proveedor = proveedores.find(p => p.id === parseInt(proveedorId));
      setSelectedProveedor(proveedor || null);
    } else {
      setSelectedProveedor(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleItemChange = (itemId, field, value) => {
    setItemsData(prev => prev.map(itemData => {
      if (itemData.item.id === itemId) {
        return {
          ...itemData,
          [field]: value
        };
      }
      return itemData;
    }));
  };

  const calcularSubtotal = () => {
    return itemsData.reduce((total, itemData) => {
      const cantidad = parseFloat(itemData.cantidad) || 0;
      const precioUnitario = parseFloat(itemData.precio_unitario) || 0;
      return total + (cantidad * precioUnitario);
    }, 0);
  };

  const calcularTotalBruto = () => {
    // Para BHE: Total Bruto es la suma de los items
    return calcularSubtotal();
  };

  const calcularRetencion = () => {
    if (formData.tipo_documento === 'BHE') {
      const totalBruto = calcularTotalBruto();
      return totalBruto * 0.145; // 14.5% de retención sobre el Total Bruto
    }
    return 0;
  };

  const calcularTotalNeto = () => {
    if (formData.tipo_documento === 'BHE') {
      const totalBruto = calcularTotalBruto();
      const retencion = calcularRetencion();
      return totalBruto - retencion; // Total Neto = Total Bruto - Retención
    }
    return 0;
  };

  const calcularIVA = () => {
    if (formData.tipo_documento === 'FE') {
      const subtotal = calcularSubtotal();
      return subtotal * 0.19; // 19% de IVA
    }
    return 0;
  };

  const calcularMontoTotal = () => {
    if (formData.tipo_documento === 'BHE') {
      return calcularTotalNeto(); // Para BHE, el total es el Total Neto
    }
    const subtotal = calcularSubtotal();
    const iva = calcularIVA();
    return subtotal + iva;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones
    if (itemsData.length === 0) {
      toast.error('No hay items seleccionados');
      return;
    }

    if (!selectedProveedor) {
      toast.error('Debes seleccionar un proveedor');
      return;
    }

    if (!formData.fecha_emision) {
      toast.error('Debes especificar la fecha de emisión');
      return;
    }

    // Validar que todos los items tengan cantidad y precio
    for (const itemData of itemsData) {
      const cantidad = parseFloat(itemData.cantidad);
      const precioUnitario = parseFloat(itemData.precio_unitario);

      if (!cantidad || cantidad <= 0) {
        toast.error(`La cantidad a solicitar para "${itemData.item.nombre}" debe ser mayor a 0`);
        return;
      }

      // Validar que no exceda la cantidad disponible
      if (cantidad > itemData.cantidadDisponible) {
        toast.error(`La cantidad a solicitar para "${itemData.item.nombre}" no puede exceder la cantidad disponible (${itemData.cantidadDisponible})`);
        return;
      }

      if (!precioUnitario || precioUnitario <= 0) {
        toast.error(`El precio unitario costo para "${itemData.item.nombre}" debe ser mayor a 0`);
        return;
      }

      // Validar que si el recurso tiene stock, se haya seleccionado una bodega
      if (itemData.tiene_stock && !itemData.bodega) {
        toast.error(`El recurso "${itemData.item.nombre}" tiene control de stock. Debe seleccionar una bodega.`);
        return;
      }
    }

    setLoading(true);
    try {
      if (ordenCompraEditando) {
        // MODO EDICIÓN: Actualizar orden de compra existente
        const ordenCompraData = {
          proveedor: selectedProveedor.id,
          descripcion: formData.descripcion || `Orden de compra para ${presupuesto.proyecto_nombre}`,
          fecha_emision: formData.fecha_emision,
          tipo_documento: formData.tipo_documento || null,
        };

        await ordenCompraService.update(ordenCompraEditando.id, ordenCompraData);

        // Actualizar o crear detalles de la orden de compra
        for (const itemData of itemsData) {
          const detalleData = {
            orden_compra: ordenCompraEditando.id,
            descripcion: itemData.item.nombre,
            cantidad: parseFloat(itemData.cantidad),
            cantidad_dias: parseFloat(itemData.item.cantidad_dias) || 1,
            unidad: itemData.item.unidad || 'unidad',
            precio_unitario: parseFloat(itemData.precio_unitario),
            bodega: itemData.tiene_stock && itemData.bodega ? parseInt(itemData.bodega) : null, // Bodega solo si tiene stock
            status: 'activo'
          };

          if (itemData.detalleOCId) {
            // Actualizar detalle existente
            await detalleOrdenCompraService.update(itemData.detalleOCId, detalleData);
          } else {
            // Crear nuevo detalle
            await detalleOrdenCompraService.create(detalleData);
          }
        }

        toast.success(`Orden de compra ${ordenCompraEditando.numero_oc} actualizada exitosamente`);
        onCreated();
      } else {
        // MODO CREACIÓN: Crear nueva orden de compra
        const ordenCompraData = {
          proyecto: presupuesto.proyecto,
          // Solo incluir presupuesto_costo si existe y es válido (no es 0 o null)
          ...(presupuesto.id && presupuesto.id > 0 ? { presupuesto_costo: presupuesto.id } : {}),
          proveedor: selectedProveedor.id,
          descripcion: formData.descripcion || `Orden de compra para ${presupuesto.proyecto_nombre}`,
          fecha_emision: formData.fecha_emision,
          tipo_documento: formData.tipo_documento || null,
          estado: 'pendiente',
          status: 'activo',
        };

        const ocResponse = await ordenCompraService.create(ordenCompraData);
        const ordenCompraCreada = ocResponse.data;

        // Crear detalles de la orden de compra y actualizar valores reales
        for (const itemData of itemsData) {
          const detalleData = {
            orden_compra: ordenCompraCreada.id,
            descripcion: itemData.item.nombre,
            cantidad: parseFloat(itemData.cantidad),
            cantidad_dias: parseFloat(itemData.item.cantidad_dias) || 1,
            unidad: itemData.item.unidad || 'unidad',
            precio_unitario: parseFloat(itemData.precio_unitario),
            status: 'activo'
          };

          // Solo incluir campos opcionales si tienen valores válidos
          if (itemData.item.id && itemData.item.id > 0 && presupuesto.id && presupuesto.id > 0) {
            // Si viene desde un presupuesto real, incluir recurso_presupuesto_costo
            detalleData.recurso_presupuesto_costo = itemData.item.id;
          }

          if (presupuesto.proyecto && presupuesto.proyecto > 0) {
            detalleData.proyecto = presupuesto.proyecto;
          }

          if (itemData.item.area_id && itemData.item.area_id > 0) {
            detalleData.area = itemData.item.area_id;
          }

          if (itemData.item.partida_id && itemData.item.partida_id > 0) {
            detalleData.partida = itemData.item.partida_id;
          }

          if (itemData.tiene_stock && itemData.bodega) {
            detalleData.bodega = parseInt(itemData.bodega);
          }

          await detalleOrdenCompraService.create(detalleData);
        }

        toast.success(`Orden de compra ${ordenCompraCreada.numero_oc} creada exitosamente con ${itemsData.length} item(s)`);
        onCreated();
      }
    } catch (error) {
      console.error('Error al procesar orden de compra:', error);
      const errorMsg = error.response?.data?.detail || 'Error al procesar orden de compra';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(value || 0);
  };

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{ordenCompraEditando ? 'Editar' : 'Crear'} Orden de Compra ({itemsSeleccionados.length} items)</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {/* Información de contexto */}
          <div className="info-box">
            <p><strong>Proyecto:</strong> {presupuesto.proyecto_nombre}</p>
            <p><strong>Presupuesto de Costo:</strong> v{presupuesto.version}</p>
          </div>

          {/* Primera fila: Tipo de Documento y Fecha de Emisión */}
          <div className="form-row">
            <div className="form-group">
              <label>Tipo de Documento</label>
              <select
                name="tipo_documento"
                value={formData.tipo_documento}
                onChange={handleInputChange}
              >
                <option value="">Seleccionar tipo de documento...</option>
                <option value="BHE">Boleta Honorario</option>
                <option value="FE">Factura Electrónica</option>
                <option value="FEE">Factura Exenta Electrónica</option>
              </select>
            </div>

            <div className="form-group">
              <label>Fecha de Emisión *</label>
              <input
                type="date"
                name="fecha_emision"
                value={formData.fecha_emision}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          {/* Selección de proveedor */}
          <div className="form-group">
            <label>Proveedor *</label>
            <select
              value={selectedProveedor?.id || ''}
              onChange={handleProveedorChange}
              required
            >
              <option value="">Seleccionar proveedor...</option>
              {Array.isArray(proveedores) && proveedores.map(proveedor => (
                <option key={proveedor.id} value={proveedor.id}>
                  {proveedor.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Información del proveedor seleccionado */}
          {selectedProveedor && (
            <div className="proveedor-info-box">
              <h4>📋 Información del Proveedor</h4>
              <div className="info-grid">
                {selectedProveedor.rut && (
                  <div className="info-item">
                    <span className="info-label">RUT:</span>
                    <span className="info-value">{selectedProveedor.rut}</span>
                  </div>
                )}
                {selectedProveedor.email && (
                  <div className="info-item">
                    <span className="info-label">Email:</span>
                    <span className="info-value">{selectedProveedor.email}</span>
                  </div>
                )}
                {selectedProveedor.telefono && (
                  <div className="info-item">
                    <span className="info-label">Teléfono:</span>
                    <span className="info-value">{selectedProveedor.telefono}</span>
                  </div>
                )}
                {selectedProveedor.contacto_principal && (
                  <div className="info-item">
                    <span className="info-label">Contacto:</span>
                    <span className="info-value">{selectedProveedor.contacto_principal}</span>
                  </div>
                )}
                {selectedProveedor.direccion && (
                  <div className="info-item">
                    <span className="info-label">Dirección:</span>
                    <span className="info-value">{selectedProveedor.direccion}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Lista de recursos seleccionados */}
          <div className="items-seleccionados-section">
            <h3>Recursos Seleccionados</h3>
            <div className="items-list">
              {itemsData.map((itemData, index) => {
                const monto = (parseFloat(itemData.cantidad) || 0) * (parseFloat(itemData.precio_unitario) || 0);

                return (
                  <div key={itemData.item.id} className="item-card">
                    <div className="item-header">
                      <h4>{index + 1}. {itemData.item.nombre}</h4>
                      <div className="item-stats">
                        <span className="stat-badge presupuestada">
                          Presupuestada: {itemData.cantidadPresupuestada}
                        </span>
                        <span className="stat-badge ejecutada">
                          Solicitada: {itemData.cantidadSolicitada.toLocaleString('es-CL')}
                        </span>
                        <span className="stat-badge pendiente">
                          Disponible: {itemData.cantidadDisponible}
                        </span>
                      </div>
                    </div>

                    <div className="item-details" style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                      <div>Área: <strong>{itemData.area_nombre}</strong></div>
                      <div>Partida: <strong>{itemData.partida_nombre}</strong></div>
                    </div>

                    <div className="item-inputs">
                      <div className="input-group">
                        <label>Cantidad a Solicitar *</label>
                        <input
                          type="number"
                          value={itemData.cantidad}
                          onChange={(e) => handleItemChange(itemData.item.id, 'cantidad', e.target.value)}
                          min="0"
                          max={itemData.cantidadDisponible}
                          step="0.01"
                          required
                          placeholder={`Máx: ${itemData.cantidadDisponible}`}
                        />
                        <small style={{ color: '#6b7280', marginTop: '4px' }}>
                          Máximo disponible: {itemData.cantidadDisponible}
                        </small>
                      </div>

                      <div className="input-group">
                        <label>Precio Unitario Costo *</label>
                        <input
                          type="number"
                          value={itemData.precio_unitario}
                          onChange={(e) => handleItemChange(itemData.item.id, 'precio_unitario', e.target.value)}
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>

                      {/* Selector de Bodega - Solo para recursos con stock */}
                      {itemData.tiene_stock && (
                        <div className="input-group">
                          <label>Bodega * <span style={{color: '#ef4444', fontSize: '12px'}}>(Recurso con stock)</span></label>
                          <select
                            value={itemData.bodega || ''}
                            onChange={(e) => handleItemChange(itemData.item.id, 'bodega', e.target.value)}
                            required
                            style={{
                              border: !itemData.bodega ? '2px solid #ef4444' : '1px solid #d1d5db'
                            }}
                          >
                            <option value="">Seleccione una bodega</option>
                            {bodegas.map(bodega => (
                              <option key={bodega.id} value={bodega.id}>
                                {bodega.nombre}
                              </option>
                            ))}
                          </select>
                          {!itemData.bodega && (
                            <small style={{ color: '#ef4444', marginTop: '4px', fontSize: '12px' }}>
                              ⚠️ Este recurso tiene control de stock. Debe seleccionar una bodega.
                            </small>
                          )}
                        </div>
                      )}

                      <div className="input-group">
                        <label>Monto Total</label>
                        <div className="monto-display-inline">
                          {formatCurrency(monto)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Monto total */}
          <div className="monto-total-section">
            {formData.tipo_documento === 'BHE' ? (
              // Desglose para Boleta de Honorarios
              <div className="monto-desglose">
                <div className="monto-row total-neto">
                  <span>Total Neto:</span>
                  <span className="monto-total-value">{formatCurrency(calcularTotalNeto())}</span>
                </div>
                <div className="monto-row retencion">
                  <span>Retención (14.5%):</span>
                  <span className="monto-value">{formatCurrency(calcularRetencion())}</span>
                </div>
                <div className="monto-row total-bruto">
                  <span>Total Bruto:</span>
                  <span className="monto-value">{formatCurrency(calcularTotalBruto())}</span>
                </div>
              </div>
            ) : formData.tipo_documento === 'FE' ? (
              // Desglose para Factura Electrónica
              <div className="monto-desglose">
                <div className="monto-row subtotal">
                  <span>Subtotal:</span>
                  <span className="monto-value">{formatCurrency(calcularSubtotal())}</span>
                </div>
                <div className="monto-row iva">
                  <span>IVA (19%):</span>
                  <span className="monto-value">{formatCurrency(calcularIVA())}</span>
                </div>
                <div className="monto-row total">
                  <span>Total a Pagar:</span>
                  <span className="monto-total-value">{formatCurrency(calcularMontoTotal())}</span>
                </div>
              </div>
            ) : (
              // Monto total simple para FEE y otros tipos
              <div className="monto-total-display">
                <span>Monto Total de la Orden:</span>
                <span className="monto-total-value">
                  {formatCurrency(calcularMontoTotal())}
                </span>
              </div>
            )}
          </div>

          {/* Descripción */}
          <div className="form-group">
            <label>Descripción</label>
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleInputChange}
              rows="3"
              placeholder={`Orden de compra para ${presupuesto.proyecto_nombre}`}
            />
          </div>

          {/* Acciones */}
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || itemsData.length === 0 || !selectedProveedor}
            >
              {loading
                ? (ordenCompraEditando ? 'Actualizando...' : 'Creando...')
                : (ordenCompraEditando ? 'Actualizar Orden de Compra' : 'Crear Orden de Compra')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

export default CrearOrdenCompraOneBaseModal;
